import React, { useRef, useEffect, useState, useLayoutEffect } from "react";
import { Dialog } from "primereact/dialog";
import PropTypes from "prop-types";
import { PortalHostProvider } from "~/context/PortalHostContext";
import "./ModalBase.css";

// Delay (in ms) to block synthetic click events from closing modal immediately after opening
// This prevents Android/tablet double-tap from opening and immediately closing the modal
const TOUCH_CLOSE_GUARD_MS = 500;

// Base modal component for consistent styling and behavior
const ModalBase = ({
  children,
  header,
  footer,
  visible,
  onHide,
  size = "medium",
  variant = "form",
  modal = true,
  resizable = false,
  maximizable = false,
  closable = true,
  className = "",
  style = {},
  contentStyle = {},
  onBeforeHide,
  hostBody = null,
  ...props
}) => {
  // Size configurations - uses min() for fluid sizing across all viewports
  const sizeConfigs = {
    tiny: { width: "min(400px, 92vw)" },
    small: { width: "min(500px, 92vw)" },
    medium: { width: "min(600px, 92vw)" },
    large: { width: "min(800px, 92vw)" },
    full: { width: "95vw", height: "90vh" },
  };

  const sizeConfig = sizeConfigs[size] || sizeConfigs.medium;

  // Track when modal was opened to guard against synthetic touch events
  const openedAtRef = useRef(null);

  // Record open time when modal becomes visible
  useEffect(() => {
    if (visible) {
      openedAtRef.current = Date.now();
    } else {
      openedAtRef.current = null;
    }
  }, [visible]);

  // No default footer — close via X button in header
  const resolvedFooter = footer || null;

  // Handle header - if it's a function, call it to get the JSX
  const headerContent = typeof header === "function" ? header() : header;

  // Create a wrapper for onHide that can be prevented
  const handleHide = (event) => {
    // Guard against synthetic touch events on Android/tablets
    // If the modal just opened, ignore close attempts that aren't from explicit button clicks
    // This prevents double-tap from opening and immediately closing the modal
    if (openedAtRef.current) {
      const timeSinceOpen = Date.now() - openedAtRef.current;
      if (timeSinceOpen < TOUCH_CLOSE_GUARD_MS) {
        // Allow explicit button clicks (Cancel, X button) - they have button/icon targets
        const target = event?.target;
        const isButtonClick =
          target &&
          (target.tagName === "BUTTON" ||
            target.closest("button") ||
            target.classList?.contains("p-dialog-header-close") ||
            target.closest(".p-dialog-header-close") ||
            target.closest(".p-button"));

        if (!isButtonClick) {
          // Block non-button close attempts (synthetic clicks, mask clicks, etc.)
          return; // Block the close
        }
      }
    }

    if (onBeforeHide) {
      const shouldHide = onBeforeHide(event);
      if (shouldHide === false) {
        return; // Prevent hiding
      }
    }
    onHide(event);
  };

  // PoC #popout: resolve the portal target from where THIS ModalBase lives —
  // a modal opened from a panel in a popout window must render in that window,
  // not in the main document.body. Re-resolved on every open so it stays
  // correct after the panel is adopted into (or back from) a popout document.
  const hostProbeRef = useRef(null);
  const [portalBody, setPortalBody] = useState(null);
  useLayoutEffect(() => {
    if (visible) {
      setPortalBody(hostProbeRef.current?.ownerDocument?.body ?? null);
    }
  }, [visible]);

  // Where the modal (and every overlay inside it) mounts. `hostBody` wins when the
  // caller knows the target window — a modal opened from the navbar for a chart that
  // was popped out lives in the main document but must render in the popout (#375).
  // Otherwise fall back to the document this ModalBase itself sits in.
  const resolvedBody = hostBody ?? portalBody ?? document.body;

  return (
    <PortalHostProvider value={resolvedBody}>
      <span
        ref={hostProbeRef}
        data-modal-host-probe=""
        style={{ display: "none" }}
      />
      <Dialog
        header={headerContent}
        visible={visible}
        onHide={handleHide}
        modal={modal}
        resizable={resizable}
        maximizable={maximizable}
        closable={closable}
        style={{
          ...sizeConfig,
          ...style,
        }}
        contentStyle={{
          padding: 0,
          overflow: "visible",
          touchAction: "auto", // Ensure touch events work properly on mobile
          ...contentStyle,
        }}
        className={`modal-base modal-${variant} modal-${size} ${className}`}
        footer={resolvedFooter}
        blockScroll={false}
        focusOnShow={false} // Prevent focus-related issues on touch devices
        dismissableMask={false} // Explicitly disable mask click closing
        closeOnEscape={false} // Disable ESC key closing to prevent accidental closes
        appendTo={resolvedBody} // Mounted in the window the modal belongs to (PoC #popout)
        {...props}
      >
        <div className="modal-content">{children}</div>
      </Dialog>
    </PortalHostProvider>
  );
};

ModalBase.propTypes = {
  children: PropTypes.node.isRequired,
  header: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.func,
  ]),
  footer: PropTypes.node,
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  size: PropTypes.oneOf(["tiny", "small", "medium", "large", "full"]),
  variant: PropTypes.oneOf(["form", "data", "simple", "warning"]),
  modal: PropTypes.bool,
  resizable: PropTypes.bool,
  maximizable: PropTypes.bool,
  closable: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  contentStyle: PropTypes.object,
  onBeforeHide: PropTypes.func,
  hostBody: PropTypes.object,
};

export default ModalBase;
